import {
  BaseAPI,
  Configuration,
  DefaultApi,
  FetchError,
  HandlerOperationAbortRequest,
  HandlerOperationListResponse,
  HandlerOperationResponse,
  HandlerOperationResponseFromJSON,
  HandlerOperationTriggerRequestRevised,
  HandlerOperationTriggerRequestToJSON,
  OperationscatalogOperation,
  instanceOfHandlerErrorResponse,
  JSONApiResponse,
  ResponseError,
} from '@internal/backstage-plugin-neo4j-aura-operations-common';
import {
  ApiRef,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/frontend-plugin-api';
import { BackendAuthApi } from '@internal/plugin-neo4j-auth-common';

import { AuraOperationsApiError } from './errors';

const AURA_OPERATIONS_API_BASE_URL = '/aura-operations-api';
const AURA_OPERATIONS_AUTH_HEADER = 'X-Omni-Authorization';
const AURA_OPERATIONS_DEFAULT_ERROR_MESSAGE = 'Unknown error';
const AURA_OPERATIONS_DEFAULT_ERROR_STATUS = 400;
const AURA_OPERATIONS_API_UNAVAILABLE_HINT =
  'Please make sure that the aura-operations-api is running.';

function auraOperationsErrorMessageFrom(error: unknown): string {
  if (error instanceof FetchError) {
    return (
      error.cause.message ??
      error.message ??
      AURA_OPERATIONS_DEFAULT_ERROR_MESSAGE
    );
  }

  return AURA_OPERATIONS_DEFAULT_ERROR_MESSAGE;
}

export class AuraOperationsRestApi extends BaseAPI {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApiBackstage: FetchApi;
  private readonly backendAuthApi: BackendAuthApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    backendAuthApi: BackendAuthApi;
  }) {
    super();

    this.discoveryApi = options.discoveryApi;
    this.fetchApiBackstage = options.fetchApi;
    this.backendAuthApi = options.backendAuthApi;
  }

  private async getConfiguration(): Promise<Configuration> {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
    const token = await this.backendAuthApi.getToken();

    return new Configuration({
      basePath: `${baseUrl}${AURA_OPERATIONS_API_BASE_URL}`,
      fetchApi: this.fetchApiBackstage.fetch,
      headers: token
        ? { [AURA_OPERATIONS_AUTH_HEADER]: `Bearer ${token}` }
        : undefined,
    });
  }

  private async getOperationsApi(): Promise<DefaultApi> {
    return new DefaultApi(await this.getConfiguration());
  }

  async getAvailableOperations(options?: {
    orchestra?: string;
    dbmsClusterId?: string;
  }): Promise<OperationscatalogOperation[]> {
    try {
      const api = await this.getOperationsApi();
      const orchestra = options?.orchestra?.trim();
      const dbmsClusterId = options?.dbmsClusterId?.trim();
      return await api.v1AvailableOperationsGet({
        orchestra: orchestra || undefined,
        dbmsClusterId: dbmsClusterId || undefined,
      });
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async getOperations(
    options: {
      limit?: number;
      databaseId?: string;
      dbmsClusterId?: string;
    } = {},
  ): Promise<HandlerOperationListResponse> {
    try {
      const api = await this.getOperationsApi();
      // requested_by is left unset on purpose. The API treats it as an optional
      // filter, so omitting it returns every run. Authentication still applies.
      return await api.v1OperationsGet({
        limit: options.limit,
        databaseId: options.databaseId,
        dbmsClusterId: options.dbmsClusterId,
      });
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async abortOperation(
    request: HandlerOperationAbortRequest,
  ): Promise<HandlerOperationResponse> {
    try {
      const api = await this.getOperationsApi();
      return await api.v1OperationsAbortPost({ body: request });
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  /**
   * Hopefully a temporary fix (issue has been reported: SRE-909):
   *
   * The generated DefaultApi serializes `parameters` through the stale
   * HandlerOperationTriggerRequestParams model, which only carries the four
   * known keys. We reuse its ToJSON handler for the typed fields, then merge
   * the operation-specific parameters onto the serialized body.
   *
   * The API itself forwards any non-empty parameters.
   */
  async triggerOperation(
    request: HandlerOperationTriggerRequestRevised,
  ): Promise<HandlerOperationResponse> {
    this.configuration = await this.getConfiguration();

    const toJSON = HandlerOperationTriggerRequestToJSON;
    const body = toJSON(request);

    if (request.additionalParameters) {
      body.parameters = {
        ...body.parameters,
        ...request.additionalParameters,
      };
    }

    try {
      const response = await this.request({
        path: '/v1/operations',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      });

      // The generated methods map JSON through `*FromJSON`; this hand-rolled
      // request has to do the same, or every field stays in wire format and the
      // camelCase model properties come back undefined.
      return new JSONApiResponse<HandlerOperationResponse>(
        response,
        HandlerOperationResponseFromJSON,
      ).value();
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  private async handleSdkError(error: unknown): Promise<never> {
    if (error instanceof ResponseError) {
      let json: unknown;

      try {
        json = await error.response.json();
      } catch (parseError) {
        // The proxy answers with plain text, such as Envoy's "upstream connect
        // error", when aura-operations-api has no ready pod. The toast would
        // otherwise show only the JSON parse failure.
        const detail =
          parseError instanceof Error
            ? parseError.message
            : AURA_OPERATIONS_DEFAULT_ERROR_MESSAGE;

        throw new AuraOperationsApiError(
          `${detail} ${AURA_OPERATIONS_API_UNAVAILABLE_HINT}`,
          error.response.status,
        );
      }

      if (
        typeof json === 'object' &&
        json !== null &&
        instanceOfHandlerErrorResponse(json)
      ) {
        const fallback = json.error ?? AURA_OPERATIONS_DEFAULT_ERROR_MESSAGE;

        throw new AuraOperationsApiError(fallback, error.response.status);
      }
    }

    throw new AuraOperationsApiError(
      auraOperationsErrorMessageFrom(error),
      AURA_OPERATIONS_DEFAULT_ERROR_STATUS,
    );
  }
}

export const auraOperationsApiRef: ApiRef<AuraOperationsRestApi> =
  createApiRef<AuraOperationsRestApi>({
    id: 'plugin.aura-operations.service',
  });
